import * as React from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import AuctionIntelligenceService from 'services/AuctionIntelligenceService';
import SettingsService from 'services/SettingsService';

// Settings for the daily Auction Intelligence Slack digest (6:45 AM PT). Reads the
// EFFECTIVE settings from the backend (stored values with defaults applied), saves
// through the generic settings store; the digest pulls them fresh on every run, so a
// save applies to the next run with no deploy.
export const ALERT_SETTINGS_NAMESPACE = 'auction-alerts';

// Slack member IDs (U…/W…), entered comma- or space-separated.
const MEMBER_ID_RE = /^[UW][A-Z0-9]{4,}$/;

function parseMentions(raw: string): string[] {
  return raw
    .split(/[\s,]+/)
    .map(s => s.trim())
    .filter(Boolean);
}

export default function AlertSettingsDialog({
  open,
  onClose,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const service = React.useMemo(() => new AuctionIntelligenceService(), []);
  const settingsService = React.useMemo(() => new SettingsService(), []);

  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [live, setLive] = React.useState(true);
  const [channel, setChannel] = React.useState('');
  const [mentions, setMentions] = React.useState('');

  // Seed from the effective settings each time the dialog opens — these reflect the
  // backend defaults until something has been saved.
  React.useEffect(() => {
    if (!open) return;
    setError(null);
    setLoading(true);
    service
      .getAlertSettings()
      .then(s => {
        if (!s) {
          setError('Failed to load current settings');
          return;
        }
        setLive(s.slackLive);
        setChannel(s.slackChannel);
        setMentions(s.alertMentions.join(', '));
      })
      .finally(() => setLoading(false));
  }, [open, service]);

  const channelClean = channel.trim().replace(/^#/, '');
  const mentionIds = parseMentions(mentions);
  const badIds = mentionIds.filter(id => !MEMBER_ID_RE.test(id));
  const canSave = !loading && !saving && channelClean !== '' && badIds.length === 0;

  const save = async () => {
    setSaving(true);
    setError(null);
    // Mentions are stored as a comma-separated STRING: Firebase drops empty arrays,
    // which would turn an explicit "tag no one" back into the backend defaults.
    const updated = await settingsService.update(ALERT_SETTINGS_NAMESPACE, {
      slackLive: live,
      slackChannel: channelClean,
      alertMentions: mentionIds.join(','),
    });
    setSaving(false);
    if (!updated) {
      setError('Failed to save settings');
      return;
    }
    onSaved();
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Alert settings</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Controls the daily Slack digest (6:45 AM PT). Changes apply from the next
          run — no deploy needed.
        </Typography>
        {loading ? (
          <Stack alignItems="center" sx={{ py: 4 }}>
            <CircularProgress size={28} />
          </Stack>
        ) : (
          <Stack spacing={3} sx={{ mt: 1 }}>
            <FormControlLabel
              control={<Switch checked={live} onChange={e => setLive(e.target.checked)} />}
              label={
                <>
                  <Typography component="span">{live ? 'Live' : 'Shadow mode'}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {live
                      ? 'The digest posts to Slack.'
                      : 'The digest only logs what it would post — nothing reaches Slack.'}
                  </Typography>
                </>
              }
            />
            <TextField
              label="Slack channel"
              value={channel}
              onChange={e => setChannel(e.target.value)}
              error={channelClean === ''}
              helperText="Channel the digest posts to (without the #). The bot must be in the channel."
              fullWidth
            />
            <TextField
              label="Tag on alerts"
              value={mentions}
              onChange={e => setMentions(e.target.value)}
              error={badIds.length > 0}
              helperText={
                badIds.length > 0
                  ? `Not Slack member IDs: ${badIds.join(', ')}`
                  : 'Slack member IDs (e.g. U06QRF0EMTL), comma-separated. Slack profile → ⋯ → Copy member ID. Leave empty to tag no one.'
              }
              fullWidth
            />
          </Stack>
        )}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" disabled={!canSave} onClick={save}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
